import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Modal, Form, Input, Tag, Select,
  message, Popconfirm, Statistic, Row, Col,
} from 'antd';
import {
  PlusOutlined, SendOutlined, BarChartOutlined,
} from '@ant-design/icons';
import { emailCampaignAPI, contactsAPI } from '../services/api';

const { TextArea } = Input;

const STATUS_MAP = {
  draft: { color: 'default', text: '草稿' },
  sending: { color: 'processing', text: '發送中' },
  sent: { color: 'green', text: '已發送' },
};

export default function EmailCampaignPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [form] = Form.useForm();
  const [contactLists, setContactLists] = useState([]);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [currentStats, setCurrentStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await emailCampaignAPI.list();
      setCampaigns(res.data);
    } catch (e) {
      message.error('載入郵件活動失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchContactLists = useCallback(async () => {
    try {
      const res = await contactsAPI.listLists();
      setContactLists(res.data);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);
  useEffect(() => { fetchContactLists(); }, [fetchContactLists]);

  const handleCreate = () => {
    setEditingCampaign(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingCampaign(record);
    form.setFieldsValue({
      name: record.name,
      subject: record.subject,
      html_body: record.html_body,
      contact_list_id: record.contact_list_id,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingCampaign) {
        await emailCampaignAPI.update(editingCampaign.id, values);
        message.success('已更新郵件活動');
      } else {
        await emailCampaignAPI.create(values);
        message.success('已建立郵件活動');
      }
      setModalOpen(false);
      fetchCampaigns();
    } catch (e) {
      if (e.errorFields) return;
      message.error(e.message || '操作失敗');
    }
  };

  const handleSend = async (id) => {
    try {
      const res = await emailCampaignAPI.send(id);
      message.success(`發送完成: ${res.data.sent} 封成功, ${res.data.failed} 封失敗`);
      fetchCampaigns();
    } catch (e) {
      message.error('發送失敗: ' + (e.message || ''));
    }
  };

  const handleViewStats = async (id) => {
    setStatsLoading(true);
    setStatsModalOpen(true);
    try {
      const res = await emailCampaignAPI.getStats(id);
      setCurrentStats(res.data);
    } catch (e) {
      message.error('載入統計失敗');
    } finally {
      setStatsLoading(false);
    }
  };

  const columns = [
    { title: '活動名稱', dataIndex: 'name', key: 'name', width: 180 },
    { title: '主旨', dataIndex: 'subject', key: 'subject', width: 220 },
    {
      title: '狀態', dataIndex: 'status', key: 'status', width: 100,
      render: (status) => {
        const s = STATUS_MAP[status] || { color: 'default', text: status };
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    {
      title: '聯絡人名單', key: 'contact_list', width: 150,
      render: (_, record) => record.contact_list ? record.contact_list.name : '-',
    },
    {
      title: '發送時間', dataIndex: 'sent_at', key: 'sent_at', width: 140,
      render: (v) => v ? new Date(v).toLocaleString('zh-TW') : '-',
    },
    {
      title: '操作', key: 'actions', width: 220,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleEdit(record)}>
            編輯
          </Button>
          <Button
            type="link"
            size="small"
            icon={<BarChartOutlined />}
            onClick={() => handleViewStats(record.id)}
          >
            統計
          </Button>
          {record.status === 'draft' && (
            <Popconfirm
              title="確定要發送此郵件活動？"
              description="發送後無法撤回"
              onConfirm={() => handleSend(record.id)}
            >
              <Button type="link" size="small" icon={<SendOutlined />}>
                發送
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="郵件行銷"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            建立郵件活動
          </Button>
        }
      >
        <Table
          dataSource={campaigns}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 900 }}
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </Card>

      {/* Create/Edit Campaign Modal */}
      <Modal
        title={editingCampaign ? '編輯郵件活動' : '建立郵件活動'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText="儲存"
        cancelText="取消"
        width={700}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="活動名稱" rules={[{ required: true, message: '請輸入活動名稱' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="subject" label="郵件主旨" rules={[{ required: true, message: '請輸入郵件主旨' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="contact_list_id" label="聯絡人名單">
            <Select
              placeholder="選擇聯絡人名單"
              allowClear
              options={contactLists.map(cl => ({
                value: cl.id,
                label: `${cl.name} (${cl.member_count || 0} 人)`,
              }))}
            />
          </Form.Item>
          <Form.Item name="html_body" label="郵件內容 (HTML)">
            <TextArea rows={10} placeholder="輸入 HTML 郵件內容" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Stats Modal */}
      <Modal
        title="郵件活動統計"
        open={statsModalOpen}
        onCancel={() => { setStatsModalOpen(false); setCurrentStats(null); }}
        footer={null}
        width={600}
      >
        {statsLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>載入中...</div>
        ) : currentStats ? (
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Statistic title="已發送" value={currentStats.total_sent} />
            </Col>
            <Col span={8}>
              <Statistic title="已開啟" value={currentStats.total_opened} />
            </Col>
            <Col span={8}>
              <Statistic title="已點擊" value={currentStats.total_clicked} />
            </Col>
            <Col span={8}>
              <Statistic title="退信" value={currentStats.total_bounced} />
            </Col>
            <Col span={8}>
              <Statistic title="開信率" value={currentStats.open_rate} suffix="%" precision={2} />
            </Col>
            <Col span={8}>
              <Statistic title="點擊率" value={currentStats.click_rate} suffix="%" precision={2} />
            </Col>
          </Row>
        ) : (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>無數據</div>
        )}
      </Modal>
    </div>
  );
}
